import { useEffect, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment-timezone';
import Table from '../../Common/components/Table';
import useShare from '../../Share/components/hooks/useShare';

const columnHelper = createColumnHelper<any>();

interface ShareJourneyStatsProps {
    activationId: number;
}

/**
 * Classic (v1) share journey rollup: per-event engagement KPIs and a
 * per-destination table (opens, visits, photo views, downloads, reshares).
 * The v2 app renders the same data via ShareJourneySection.
 */
export default function ShareJourneyStats({
    activationId,
}: ShareJourneyStatsProps) {
    const { getJourneySummary, getJourneyDestinations } = useShare();
    const [summary, setSummary] = useState<any>(null);
    const [destinations, setDestinations] = useState<any[]>([]);

    useEffect(() => {
        let mounted = true;
        Promise.all([
            getJourneySummary(activationId).catch(() => null),
            getJourneyDestinations(activationId).catch(() => []),
        ]).then(([s, d]) => {
            if (!mounted) return;
            setSummary(s);
            setDestinations(d ?? []);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    if (!summary || !summary.uniqueDestinations) {
        return null;
    }

    const openRate = Math.round((summary.openRate ?? 0) * 100);

    const kpis: [string, string | number][] = [
        ['Reached', summary.uniqueDestinations],
        ['Opened', `${summary.openedDestinations} (${openRate}%)`],
        ['Visits', summary.totalVisits],
        ['Photos viewed', summary.mediaViews],
        ['Downloads', summary.downloads],
        ['Reshares', summary.reshares],
        ['CTA clicks', summary.ctaClicks],
    ];

    const columns = [
        columnHelper.accessor('destination', {
            id: 'destination',
            header: () => <span>destination</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('type', {
            id: 'type',
            header: () => <span>type</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue() === 1 ? 'text' : 'email'}</span>
                </div>
            ),
        }),
        columnHelper.accessor('visits', {
            id: 'visits',
            header: () => <span>visits</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('mediaViews', {
            id: 'mediaViews',
            header: () => <span>photos viewed</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('downloads', {
            id: 'downloads',
            header: () => <span>downloads</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('reshares', {
            id: 'reshares',
            header: () => <span>reshares</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('lastSeenAt', {
            id: 'lastSeenAt',
            header: () => <span>last seen</span>,
            cell: (info) => (
                <div className="row">
                    <span>
                        {info.getValue()
                            ? moment(info.getValue()).format('MMM D, h:mm A')
                            : '—'}
                    </span>
                </div>
            ),
        }),
    ];

    return (
        <>
            <div className="pt-10 mx-auto flex max-w-10xl flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
                <h1 className="mt-1 text-xl font-semibold leading-6 text-gray-900">
                    Share Journey
                </h1>
            </div>
            <div className="mx-auto flex max-w-10xl flex-wrap gap-8 px-4 pb-4 pt-4 sm:px-6 lg:px-8">
                {kpis.map(([label, value]) => (
                    <div key={label}>
                        <div className="text-2xl font-semibold text-gray-900">
                            {typeof value === 'number'
                                ? value.toLocaleString()
                                : value}
                        </div>
                        <div className="text-sm text-gray-500">{label}</div>
                    </div>
                ))}
            </div>
            <Table columns={columns} data={destinations} />
        </>
    );
}
